import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
  Typography,
  Button,
} from '@ui/components';
import { cdnEmployeesTemplatePath, getCdnGuidelinesPath } from '@shared/utils';
import { useEmployeesMassImportStore } from '@pages/employees/stores';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { FileUploader } from './file-uploader/file-uploader';

export const EmployeesMassImportFileUpload: FC<{
  uploadFile: (file: File) => void;
}> = ({ uploadFile }) => {
  const { t } = useTranslation();
  const { file, isUploadFilePending, fileUploadError, resetFile, showSummary } =
    useEmployeesMassImportStore();

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>{t('EMPLOYEES.MASS_IMPORT.TITLE')}</DialogTitle>
      </DialogHeader>

      <section className="p-4 space-y-8">
        <div className="space-y-4">
          <Typography variant="paragraphBasicBook">
            {t('EMPLOYEES.MASS_IMPORT.DESCRIPTION_1')}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {t('EMPLOYEES.MASS_IMPORT.DESCRIPTION_2')}
          </Typography>

          <Typography variant="paragraphBasicBook">
            {t('EMPLOYEES.MASS_IMPORT.DESCRIPTION_3')}
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="secondary" asChild>
            <a href={cdnEmployeesTemplatePath} rel="noreferrer" target="_blank">
              {t('EMPLOYEES.MASS_IMPORT.DOWNLOAD_CSV_TEMPLATE')}
            </a>
          </Button>

          <Button variant="link" asChild>
            <a href={getCdnGuidelinesPath()} rel="noreferrer" target="_blank">
              {t('EMPLOYEES.MASS_IMPORT.SHOW_PDF_INSTRUCTIONS')}
            </a>
          </Button>
        </div>

        <div className="space-y-4">
          <Typography variant="paragraphBasicBook" className="mt-4">
            {t('EMPLOYEES.MASS_IMPORT.FIELDS_DESCRIPTION')}
          </Typography>
          <div>
            <Typography variant="paragraphBasicBook" className="mb-2">
              {t('EMPLOYEES.MASS_IMPORT.FIELDS.TITLE')}
            </Typography>
            <ul className="list-disc pl-6">
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.FIRST_NAME')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.LAST_NAME')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.COUNTRY_CODE')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.PHONE_NUMBER')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.COST_CENTER')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.PROFILES')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.EMAIL')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.LABELS')}
                </Typography>
              </li>
              <li>
                <Typography variant="paragraphBasicBook">
                  {t('EMPLOYEES.MASS_IMPORT.FIELDS.ASSIGNED_VEHICLES')}
                </Typography>
              </li>
            </ul>
          </div>
        </div>

        <FileUploader
          fileChange={(file) => {
            if (!file) return resetFile();
            uploadFile(file);
          }}
          errorMessage={fileUploadError ?? undefined}
          isPending={isUploadFilePending}
          file={file}
        />
      </section>
      {file && !isUploadFilePending && (
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="tertiary" size="large" wide>
              {t('EMPLOYEES.MASS_IMPORT.CANCEL')}
            </Button>
          </DialogClose>

          <Button onClick={showSummary} variant="primary" size="large" wide>
            {t('EMPLOYEES.MASS_IMPORT.UPLOAD')}
          </Button>
        </DialogFooter>
      )}
    </DialogContent>
  );
};
